import { useEffect, useState } from "react";
import { CheckIcon, CircleIcon, LoaderCircleIcon } from "lucide-react";

import {
  WorkerSetupProblem,
  WorkerSetupStage,
  type WorkerSetup,
} from "@/gen/nodebase/v1/worker_pb";

// A new Worker's first setup, as the server reports it
// (docs/worker-setup-progress.md). Every step shown is a record the control
// plane has already written; the only thing this adds is the clock between
// two polls, counted from the server's own elapsed figures.

const STEPS = [
  {
    stages: [WorkerSetupStage.QUEUED, WorkerSetupStage.PREPARING],
    label: "Build your machine",
    detail: "Encrypted disk and system image — the longest step, about a minute.",
  },
  {
    stages: [WorkerSetupStage.STARTING],
    label: "Start it up",
    detail: "Booting and waiting for the Worker to check in.",
  },
  {
    stages: [WorkerSetupStage.CONNECTING],
    label: "Connect it to your network",
    detail: "Setting up its private address and the regional gateway.",
  },
] as const;

// useNow ticks once a second so the elapsed figures move between polls.
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function WorkerSetupProgress({
  setup,
  readAt,
  workerId,
}: {
  setup: WorkerSetup;
  // When this reading arrived (ms since epoch, the browser's clock): the
  // server's elapsed seconds are as of that moment.
  readAt: number;
  workerId: string;
}) {
  const since = (useNow() - readAt) / 1000;
  const current = STEPS.findIndex((step) =>
    (step.stages as readonly WorkerSetupStage[]).includes(setup.stage),
  );
  const stopped = setup.problem === WorkerSetupProblem.STOPPED;

  let problem: { tone: "warning" | "danger"; text: string } | null = null;
  switch (setup.problem) {
    case WorkerSetupProblem.RETRYING: {
      const wait = setup.retryInSeconds - since;
      problem = {
        tone: "warning",
        text:
          wait > 1
            ? `The last attempt didn't finish. Trying again automatically in ${Math.ceil(wait)} s — nothing for you to do.`
            : "The last attempt didn't finish. Trying again automatically now — nothing for you to do.",
      };
      break;
    }
    case WorkerSetupProblem.SLOW:
      problem = {
        tone: "warning",
        text: `This is taking much longer than usual. It is still being tried and you don't need to do anything, but please email support@nodebase.com with your Worker ID (${workerId}) so we can look.`,
      };
      break;
    case WorkerSetupProblem.STOPPED:
      problem = {
        tone: "danger",
        text: `Setup stopped and needs our help. Email support@nodebase.com with your Worker ID (${workerId}); your account and subscription are unaffected.`,
      };
      break;
  }

  return (
    <div
      data-testid="worker-setup"
      data-stage={WorkerSetupStage[setup.stage]}
      className="mb-4 rounded-md border bg-secondary/40 p-3 text-sm"
    >
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-medium">
          {stopped ? "Worker setup stopped" : "Setting up your Worker"}
        </p>
        <p data-testid="worker-setup-elapsed" className="text-xs tabular-nums text-muted-foreground">
          {clock(setup.elapsedSeconds + since)}
          {setup.problem === WorkerSetupProblem.SLOW || stopped ? "" : " · usually about 2 minutes"}
        </p>
      </div>
      <ol className="space-y-2">
        {STEPS.map((step, i) => {
          const done = i < current;
          const active = i === current;
          return (
            <li
              key={step.label}
              data-testid="worker-setup-step"
              data-state={done ? "done" : active ? "active" : "pending"}
              className="flex items-start gap-2"
            >
              <span className="mt-0.5 shrink-0">
                {done ? (
                  <CheckIcon className="size-4" style={{ color: "var(--success)" }} aria-label="done" />
                ) : active && !stopped ? (
                  <LoaderCircleIcon className="size-4 animate-spin text-primary" aria-label="in progress" />
                ) : (
                  <CircleIcon className="size-4 text-muted-foreground/50" aria-label={active ? "stopped" : "not started"} />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className={done || active ? "" : "text-muted-foreground"}>{step.label}</span>
                  {active && !stopped && (
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {clock(setup.stageElapsedSeconds + since)}
                    </span>
                  )}
                </div>
                {active && (
                  <p className="text-xs text-muted-foreground">
                    {setup.stage === WorkerSetupStage.QUEUED
                      ? `Waiting for ${setup.queueAhead} other ${setup.queueAhead === 1 ? "Worker" : "Workers"} in this region to finish first.`
                      : step.detail}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {problem && (
        <p
          data-testid="worker-setup-problem"
          className={`nb-alert nb-alert--${problem.tone} mt-3 text-xs`}
        >
          {problem.text}
        </p>
      )}
      {!stopped && (
        <p className="mt-3 text-xs text-muted-foreground">
          You can leave this page; setup continues without it. Meanwhile, download
          Desktop to connect once it's ready.
        </p>
      )}
    </div>
  );
}
